import request from "../../utils/request";
import { Result } from "../../models/general";

export enum BulkUploadMethod {
  Manual = 0,
  OCR = 1,
}

export enum BulkUploadJobStatus {
  Created = 0,
  Uploading = 1,
  Processing = 2,
  Completed = 3,
  Failed = 4,
  OcrProcessing = 5,
}

export enum BulkUploadDocumentStatus {
  Uploaded = 0,
  Mapped = 1,
  Validated = 2,
  Processing = 3,
  Completed = 4,
  Failed = 5,
  OcrProcessing = 6,
  OcrCompleted = 7,
}

export interface BulkUploadJobView {
  id: number;
  method: BulkUploadMethod;
  status: BulkUploadJobStatus;
  totalFiles: number;
  processedFiles: number;
  successfulFiles: number;
  failedFiles: number;
  createdOn: string;
  documents?: BulkUploadDocumentView[];
}

export interface BulkUploadDocumentView {
  id: number;
  bulkUploadJobId: number;
  originalFileName: string;
  fileSize: number;
  contentType: string;
  status: BulkUploadDocumentStatus;
  userId?: string;
  userFullName?: string;
  companyAttachmentTypeId?: number;
  companyAttachmentTypeName?: string;
  ocrDetectedName?: string;
  ocrDetectedType?: string;
  ocrConfidence?: number;
  errorMessage?: string;
  previewUrl?: string;
  extractedFieldsJson?: string;
  matchReasoning?: string;
  attachmentTypeAutoCreated?: boolean;
  expiryDate?: string;
  reminderDays?: number;
}

export async function createBulkUploadJob(method: BulkUploadMethod) {
  const response = await request.post<Result<BulkUploadJobView>>(
    "BulkUpload/CreateJob",
    { method }
  );
  return response.data;
}

export async function getBulkUploadJob(jobId: number) {
  const response = await request.get<Result<BulkUploadJobView>>(
    `BulkUpload/GetJob/${jobId}`
  );
  return response.data;
}

export async function getBulkUploadJobs() {
  const response = await request.get<Result<BulkUploadJobView[]>>(
    "BulkUpload/GetJobs"
  );
  return response.data;
}

export async function uploadBulkFiles(jobId: number, files: File[]) {
  const formData = new FormData();
  files.forEach((file) => formData.append("files", file));
  const response = await request.post<Result<BulkUploadJobView>>(
    `BulkUpload/UploadFiles/${jobId}`,
    formData,
    { headers: { "Content-Type": "multipart/form-data" } }
  );
  return response.data;
}

export async function deleteBulkUploadJob(jobId: number) {
  const response = await request.post<Result<boolean>>(
    `BulkUpload/DeleteJob/${jobId}`
  );
  return response.data;
}

export async function removeBulkUploadDocument(documentId: number) {
  const response = await request.post<Result<boolean>>(
    `BulkUpload/RemoveDocument/${documentId}`
  );
  return response.data;
}

export interface MapDocumentInput {
  documentId: number;
  userId: string;
  companyAttachmentTypeId: number;
  expiryDate?: string;
  reminderDays?: number;
}

export interface BulkUploadValidationView {
  documentId: number;
  originalFileName: string;
  validationStatus: 'Valid' | 'Warning' | 'Error';
  messages: string[];
}

export interface BulkUploadValidationSummaryView {
  validCount: number;
  warningCount: number;
  errorCount: number;
  documents: BulkUploadValidationView[];
}

export interface EmployeeSimpleView {
  id: string;
  fullName: string;
  department?: string;
}

export interface CompanyAttachmentTypeView {
  id: number;
  attachmentType: string;
  reminderDays: number;
}

export interface ExtractedDocumentFields {
  fullNameEnglish?: string;
  fullNameArabic?: string;
  nationality?: string;
  dateOfBirth?: string;
  gender?: string;
  maritalStatus?: string;
  documentNumber?: string;
  issueDate?: string;
  expiryDate?: string;
  placeOfIssue?: string;
  emiratesIdNumber?: string;
  unifiedNumber?: string;
  passportNumber?: string;
  mohrePermitNumber?: string;
  employerName?: string;
  occupation?: string;
  salary?: number;
  salaryCurrency?: string;
}

export async function mapBulkUploadDocuments(
  jobId: number,
  mappings: MapDocumentInput[]
) {
  const response = await request.post<Result<BulkUploadJobView>>(
    `BulkUpload/MapDocuments/${jobId}`,
    { mappings }
  );
  return response.data;
}

export async function validateBulkUploadJob(jobId: number) {
  const response = await request.get<Result<BulkUploadValidationSummaryView>>(
    `BulkUpload/ValidateJob/${jobId}`
  );
  return response.data;
}

export async function processBulkUploadJob(jobId: number) {
  const response = await request.post<Result<BulkUploadJobView>>(
    `BulkUpload/ProcessJob/${jobId}`
  );
  return response.data;
}

export async function getEmployeesForMapping() {
  const response = await request.get<Result<EmployeeSimpleView[]>>(
    'BulkUpload/GetEmployees'
  );
  return response.data;
}

export async function getAttachmentTypes() {
  const response = await request.get<Result<CompanyAttachmentTypeView[]>>(
    'BulkUpload/GetAttachmentTypes'
  );
  return response.data;
}

export async function runBulkUploadOcr(jobId: number) {
  const response = await request.post<Result<BulkUploadJobView>>(
    `BulkUpload/RunOcr/${jobId}`
  );
  return response.data;
}
